import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onGetStartedClick: () => void;
}

export default function HeroSection({ onGetStartedClick }: HeroSectionProps) {
  return (
    <section className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-64 h-64 bg-primary rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">Join 10,000+ Creators</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-text-primary leading-tight">
              Every Voice Has a <span className="text-primary">Story Worth Sharing</span>
            </h1>

            <p className="text-lg text-text-secondary leading-relaxed">
              Transform your storytelling passion into a thriving career. Join India's fastest-growing audio content platform and reach millions of listeners across the country.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onGetStartedClick}
                className="flex items-center space-x-2 px-8 py-4 bg-cta text-cta-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-cta/90 hover:shadow-warm-lg hover:scale-105">

                <span>Start Creating Today</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>

              <button className="flex items-center space-x-2 px-8 py-4 bg-card text-text-primary border-2 border-primary rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary hover:text-primary-foreground">
                <Icon name="PlayIcon" size={20} />
                <span>Watch Success Stories</span>
              </button>
            </div>

            <div className="flex items-center space-x-8 pt-4">
              <div className="text-center">
                <div className="text-3xl font-heading font-bold text-primary">₹50L+</div>
                <div className="text-sm text-text-secondary">Paid to Creators</div>
              </div>
              <div className="w-px h-12 bg-border" />
              <div className="text-center">
                <div className="text-3xl font-heading font-bold text-primary">10M+</div>
                <div className="text-sm text-text-secondary">Monthly Listeners</div>
              </div>
              <div className="w-px h-12 bg-border" />
              <div className="text-center">
                <div className="text-3xl font-heading font-bold text-primary">15+</div>
                <div className="text-sm text-text-secondary">Languages</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-warm-lg">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1c840df7b-1767730242512.png"
                alt="Young Indian woman wearing headphones recording podcast in professional studio with microphone and laptop"
                className="w-full h-[500px] object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              
              <div className="absolute bottom-6 left-6 right-6 bg-card/95 backdrop-blur-sm rounded-xl p-4 shadow-warm">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center">
                    <Icon name="MicrophoneIcon" size={24} className="text-success-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-text-primary">Live Recording</div>
                    <div className="text-xs text-text-secondary">2,847 listeners tuned in</div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1 h-8 bg-success rounded-full animate-pulse" />
                    <div className="w-1 h-6 bg-success rounded-full animate-pulse delay-75" />
                    <div className="w-1 h-10 bg-success rounded-full animate-pulse delay-150" />
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -right-6 w-32 h-32 bg-accent rounded-full opacity-20 blur-2xl" />
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-primary rounded-full opacity-20 blur-2xl" />
          </div>
        </div>
      </div>
    </section>);

}